import authService from "../services/authService";
import { canAccessProfilesMenu, extractRolesFromUser } from "./authorization";
import { validateAuthSession } from "./authSession";

const PUBLIC_PATHS = new Set(["/", "/login", "/callback"]);

export const isPublicAppPath = (pathname: string) => PUBLIC_PATHS.has(pathname);

export const resolvePreferredUsername = (user: any): string | undefined => {
  const profile = user?.profile || {};
  const candidates = [profile.preferred_username, profile.username, profile.email];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return undefined;
};

export type ProfilesAccess = {
  menuVisible: boolean;
  canManageSourceSystems: boolean;
};

export const resolveProfilesAccess = (user: any | null, roles: string[]): ProfilesAccess => {
  const allowed = user ? canAccessProfilesMenu(roles, user) : false;
  return {
    menuVisible: allowed,
    canManageSourceSystems: allowed,
  };
};

export type AuthBootstrapResult = {
  user: any | null;
  roles: string[];
  profilesMenuVisible: boolean;
  profilesCanManageSourceSystems: boolean;
  sessionValid: boolean;
};

const emptyBootstrapResult = (): AuthBootstrapResult => ({
  user: null,
  roles: [],
  profilesMenuVisible: false,
  profilesCanManageSourceSystems: false,
  sessionValid: false,
});

const buildBootstrapResult = (user: any): AuthBootstrapResult => {
  const roles = extractRolesFromUser(user);
  const profilesAccess = resolveProfilesAccess(user, roles);
  return {
    user,
    roles,
    profilesMenuVisible: profilesAccess.menuVisible,
    profilesCanManageSourceSystems: profilesAccess.canManageSourceSystems,
    sessionValid: true,
  };
};

const loadAuthenticatedUser = async (
  maxAttempts = 8,
  retryDelayMs = 350,
): Promise<AuthBootstrapResult> => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const candidate = await authService.getUser();

    if (!candidate) {
      await new Promise((resolve) => {
        setTimeout(resolve, retryDelayMs);
      });
      continue;
    }

    const session = await validateAuthSession(candidate);
    if (session.valid && session.user) {
      return buildBootstrapResult(session.user);
    }

    return emptyBootstrapResult();
  }

  return emptyBootstrapResult();
};

export const bootstrapAuthenticatedUser = loadAuthenticatedUser;

export const refreshAuthFromSession = async (): Promise<AuthBootstrapResult> => {
  return loadAuthenticatedUser();
};
